<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    :title="userDirty.id ? 'Edit user' : 'Add new user'"
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <div class="mb-2 font-weight-bold">
          Name
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="userDirty.name"
            outlined
            :error-messages="errors"
            placeholder="Enter name"
          />
        </validation-provider>

        <div class="mb-2 font-weight-bold">
          Username
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="userDirty.username"
            outlined
            :error-messages="errors"
            placeholder="Enter username"
          />
        </validation-provider>

        <div class="mb-2 font-weight-bold">
          Email
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="email"
        >
          <v-text-field
            v-model="userDirty.email"
            outlined
            :error-messages="errors"
            placeholder="Enter user email"
          />
        </validation-provider>

        <div class="mb-2 font-weight-bold">
          Password
        </div>
        <validation-provider
          v-slot="{ errors }"
          :rules="{ required: !userDirty.id, min: 3 }"
        >
          <v-text-field
            v-model="userDirty.password"
            outlined
            type="password"
            :error-messages="errors"
            placeholder="Enter user password"
          />
        </validation-provider>

        <div class="mb-2 font-weight-bold">
          Role
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-select
            v-model="userDirty.role_id"
            outlined
            :items="roles.data"
            :loading="roles.loading"
            item-value="id"
            item-text="title"
            :error-messages="errors"
            placeholder="Select role"
          />
        </validation-provider>

        <div v-if="selectedRole && (selectedRole.name === 'admin' || selectedRole.name === 'user' || selectedRole.name === 'stream-listener')">
          <div class="mb-2 font-weight-bold">
            Client
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-autocomplete
              v-model="userDirty.client_id"
              solo
              flat
              outlined
              persistent-hint
              :items="clients.data"
              :loading="clients.loading"
              clearable
              item-text="name"
              item-value="id"
              placeholder="None"
              :error-messages="errors"
            />
          </validation-provider>
        </div>

        <div v-if="selectedRole && selectedRole.name === 'stream-listener'">
          <div class="mb-2 font-weight-bold">
            Streaming Settings
          </div>

          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="userDirty.max_concurrent_listeners"
              solo
              flat
              outlined
              type="number"
              label="Maximum Concurrent Listeners"
              hint="Specifies how many devices can listen to streams at the same time"
              :error-messages="errors"
            />
          </validation-provider>

          <div class="mb-2 font-weight-bold">
            Domain
          </div>

          <div v-if="userDirty.id">
            {{ userDirty.domain ? userDirty.domain : '-' }}
          </div>
          <div v-else>
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-autocomplete
                v-model="userDirty.domain"
                solo
                flat
                outlined
                persistent-hint
                :items="domains.data"
                :loading="domains.loading"
                clearable
                item-text="domain"
                item-value="domain"
                placeholder="None"
                :error-messages="errors"
              />
            </validation-provider>
          </div>
        </div>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="userDirty.id"
          color="grey"
          outlined
          @click="toggleConfirmationDialog(true)"
        >
          <v-icon
            left
            small
          >
            fa fa-trash
          </v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deleteUser()"
    />
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import UserModel from '@/models/UserModel'
import Users from '@/services/Users'
import Roles from '@/services/Roles'
import Clients from '@/services/Clients'

export default {
  name: 'EditUserDialog',
  components: { ConfirmDeleteDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    user: {
      type: Object,
      default: () => cloneDeep(UserModel.emptyModel)
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userDirty: cloneDeep(this.user),
      submitting: false,
      roles: {
        loading: false,
        data: []
      },
      clients: {
        loading: false,
        data: []
      },
      domains: {
        loading: false,
        data: [
          { domain: 'jukeboxmusic.eu' },
          { domain: 'jukebox.hr' },
          { domain: 'musicboxbasic.com' }
        ]
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    selectedRole() {
      if (!this.userDirty.role_id) return null

      const role = this.roles.data.find(r => r.id === this.userDirty.role_id)

      return role || null
    }
  },
  watch: {
    modalVisible(visible) {
      if (!visible) return

      this.userDirty = cloneDeep(this.user)

      this.userDirty.role_id = this.user.role ? this.user.role.id : null
    }
  },
  created() {
    this.fetchRoles()
    this.fetchClients()
  },
  methods: {
    async fetchRoles() {
      this.roles.loading = true

      try {
        this.roles.data = await Roles.fetch()
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Error getting roles',
          color: 'error'
        })
        throw e
      } finally {
        this.roles.loading = false
      }
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    },
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          name: this.userDirty.name,
          domain: this.userDirty.domain === 'default' ? null : this.userDirty.domain,
          username: this.userDirty.username === '' ? null : this.userDirty.username,
          email: this.userDirty.email,
          password: this.userDirty.password,
          role_id: this.userDirty.role_id,
          client_id: this.userDirty.client_id,
          max_concurrent_listeners: this.userDirty.max_concurrent_listeners
        }

        if (this.userDirty.id) {
          const result = await Users.update(this.userDirty.id, data)

          this.$emit('user:updated', result)

          this.$store.dispatch('alert', {
            text: 'User successfully updated',
            color: 'success'
          })
        } else {
          const result = await Users.store(data)

          this.$emit('user:created', result)

          this.$store.dispatch('alert', {
            text: 'User successfully created',
            color: 'success'
          })
        }

        this.modalVisible = false

        this.$refs.validation.reset()
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting user',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteUser() {
      this.deleting = true

      try {
        const result = await Users.delete(this.userDirty.id)

        this.$store.dispatch('alert', {
          text: 'Successfully deleted',
          color: 'success'
        })

        this.modalVisible = false
        this.$emit('user:deleted', result)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    }
  }
}
</script>
